import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Video } from "lucide-react";
import { CATALOGUE, OCCASION_FAQS } from "./landing/data";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";
import { WaitlistModal } from "./landing/WaitlistModal";
import { openBooking, bookingUrl, trackBooking } from "@/lib/booking";

const slugify = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const ServiceCard = ({ s, openWaitlist }) => (
  <article className="flex h-full flex-col rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] shadow-sm transition-shadow hover:shadow-md" data-testid={`occasion-card-${slugify(s.en)}`}>
    <div className="h-44 overflow-hidden rounded-t-[14px] bg-[#F3EADA]/50">
      <a href={bookingUrl(slugify(s.en))} target="_blank" rel="noopener noreferrer" onClick={() => trackBooking({ location: "occasions-pooja-card", pooja: slugify(s.en) })} aria-label={`Book ${s.en}`} className="block h-full w-full">
        <img src={`/pooja-art/${slugify(s.en)}.jpg`} alt={`${s.en} illustration`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
      </a>
    </div>
    <div className="flex flex-1 flex-col p-5">
      <div className="flex items-start gap-3">
        <s.Icon size={32} />
        <div>
          <p className="font-baloo text-base text-[#C99B55]">{s.kn}</p>
          <h3 className="font-eczar text-lg font-semibold text-[#3B221B]">{s.en}</h3>
        </div>
      </div>
      <ul className="mt-3 flex-1 space-y-1.5">
        {s.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 font-mukta text-sm text-[#6B5044]">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#B23A2B]" aria-hidden="true" />{b}
          </li>
        ))}
      </ul>
      <button onClick={() => openWaitlist(slugify(s.en))} data-testid={`occasion-cta-${slugify(s.en)}`}
        className="mt-4 inline-flex items-center gap-1 font-mukta text-sm font-semibold text-[#A8722A] hover:text-[#8f5f21]">
        Book now <ArrowRight size={14} />
      </button>
    </div>
  </article>
);

export default function OccasionsPage() {
  const [waitlist, setWaitlist] = useState({ open: false, role: "Family" });
  const openWaitlist = (pooja) => openBooking({ location: "occasions", pooja: typeof pooja === "string" ? pooja : "" });
  const groups = Object.entries(CATALOGUE);

  useSeo({
    title: "Purohith Services by Occasion — ಶುಭ ಕಾರ್ಯಗಳು | Griha Pravesha, Homas & Poojas in Bengaluru | PurohithConnect",
    description: "Browse purohith services in Bengaluru by occasion — Griha Pravesha, Naamakarana, Vivaha, Satyanarayana Pooja, Rudrabhisheka, Ganapathi & Navagraha Homa, Graha Shanti and more. Every tradition honoured.",
    canonicalPath: "/occasions",
    jsonLd: useMemo(() => ([
      {
        "@context": "https://schema.org", "@type": "ItemList", name: "Purohith services by occasion",
        itemListElement: Object.values(CATALOGUE).flat().map((s, i) => ({ "@type": "ListItem", position: i + 1, name: s.en })),
      },
      {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: OCCASION_FAQS.map((x) => ({ "@type": "Question", name: x.q, acceptedAnswer: { "@type": "Answer", text: x.a } })),
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://purohithconnect.com/" },
          { "@type": "ListItem", position: 2, name: "Occasions", item: "https://purohithconnect.com/occasions" },
        ],
      },
    ]), []),
  });

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
      <SiteHeader openWaitlist={openWaitlist} />
      <main className="mx-auto max-w-7xl px-4 pb-20 md:px-8">
        <section className="pt-12 text-center">
          <p className="font-baloo text-base text-[#B23A2B]">ಶುಭ ಕಾರ್ಯಗಳು</p>
          <h1 className="mt-1 font-eczar text-4xl font-bold sm:text-5xl" data-testid="occasions-title">Every sacred occasion, done right</h1>
          <p className="mx-auto mt-3 max-w-2xl font-mukta text-base text-[#6B5044] md:text-lg">
            From a new home to a new name, ceremonies to homas to shanti rituals — find a KYC-verified purohith who performs your occasion in your language and your tradition.
          </p>
        </section>

        {groups.map(([cat, items]) => (
          <section key={cat} className="mt-12" data-testid={`occasion-group-${slugify(cat)}`}>
            <h2 className="font-eczar text-2xl font-semibold text-[#3B221B]">{cat}</h2>
            <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((s) => <ServiceCard key={s.en} s={s} openWaitlist={openWaitlist} />)}
            </div>
          </section>
        ))}

        <section className="mt-14 grid items-center gap-8 rounded-[20px] border border-[#A8722A]/25 bg-[#F3EADA] p-6 md:grid-cols-[1fr_auto] md:p-8" data-testid="occasions-epooja-note">
          <div>
            <div className="flex items-center gap-2 text-[#B23A2B]">
              <Video size={18} />
              <p className="font-mukta text-xs font-semibold uppercase tracking-widest">Far from home?</p>
            </div>
            <h2 className="mt-2 font-eczar text-2xl font-semibold text-[#3B221B]">E-pooja from Bengaluru, in your name</h2>
            <p className="mt-1 font-baloo text-base text-[#A8722A]">ಎಲ್ಲಿದ್ದರೂ ಆಶೀರ್ವಾದ ನಿಮ್ಮೊಂದಿಗೆ</p>
            <p className="mt-3 font-mukta text-sm text-[#6B5044] md:text-base">
              For families abroad or in other cities — sankalpa taken in your name and gotra, join live on video or receive the recording, and prasada couriered to your doorstep, performed by the same verified purohiths.
            </p>
          </div>
          <button onClick={() => openWaitlist("Family")} data-testid="occasions-epooja-cta"
            className="w-full shrink-0 rounded-full bg-[#B23A2B] px-6 py-3 font-mukta font-semibold text-[#FFFDF8] hover:bg-[#9a2f22] md:w-auto">
            Get early access
          </button>
        </section>

        <div className="mt-10 rounded-[14px] border border-[#3E6B4F]/30 p-5 font-mukta text-sm text-[#3E6B4F]" style={{ backgroundColor: "rgba(62,107,79,0.07)" }}>
          Samagri, your way — ask your purohith to bring everything, or receive the complete list in advance and arrange it yourself. You'll see the full details on your booking summary before you confirm.
        </div>

        <section className="mt-14" data-testid="occasions-faq-section">
          <div className="text-center">
            <p className="font-baloo text-base text-[#B23A2B]">ಪ್ರಶ್ನೋತ್ತರ</p>
            <h2 className="mt-1 font-eczar text-2xl font-semibold text-[#3B221B] md:text-3xl">Booking a purohith — your questions, answered</h2>
          </div>
          <div className="mx-auto mt-6 max-w-3xl space-y-3">
            {OCCASION_FAQS.map((x, i) => (
              <details key={i} className="group rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-4 shadow-sm [&_summary::-webkit-details-marker]:hidden" data-testid={`occasions-faq-${i}`} {...(i === 0 ? { open: true } : {})}>
                <summary className="flex cursor-pointer items-center justify-between gap-3 font-eczar text-[15px] font-semibold text-[#3B221B]">
                  {x.q}
                  <ChevronDown size={18} className="shrink-0 text-[#A8722A] transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-2 font-mukta text-sm text-[#6B5044]">{x.a}</p>
              </details>
            ))}
          </div>
        </section>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <Link to="/traditions" data-testid="occasions-to-traditions" className="inline-flex items-center gap-1.5 font-mukta font-semibold text-[#A8722A] hover:text-[#8f5f21]">See traditions we honour <ArrowRight size={16} /></Link>
          <Link to="/festivals" data-testid="occasions-to-festivals" className="inline-flex items-center gap-1.5 font-mukta font-semibold text-[#A8722A] hover:text-[#8f5f21]">Festival Calendar 2026 <ArrowRight size={16} /></Link>
        </div>
      </main>
      <SiteFooter />
      <WaitlistModal key={`${waitlist.open}-${waitlist.role}`} open={waitlist.open} defaultRole={waitlist.role}
        onClose={() => setWaitlist((w) => ({ ...w, open: false }))} />
    </div>
  );
}
